import React, { memo, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import useDropdownPosition from '../../../hooks/useDropdownPosition';
import AddFabricationLocationModal from './AddFabricationLocationModal';

const ADD_NEW_VALUE = '__ADD_NEW__';

/**
 * FabricationShopCell - Portal-based dropdown cell for fabrication shop selection with drag-to-fill
 * Uses portal rendering to avoid clipping issues in virtualized tables.
 */
const FabricationShopCell = memo(({
  value,
  rowIndex,
  rowId,
  columnId,
  hasEditPermission = false,
  locations = [],
  onUpdate,
  onSave,
  isDragging = false,
  onDragFillStart,
  showDragHandle = true,
  isRetrying = false,
  onLocationAdded
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const triggerRef = useRef(null);
  const getPosition = useDropdownPosition({ menuHeight: 250, menuWidth: 160 });

  const handleSelect = useCallback((newValue) => {
    if (newValue === ADD_NEW_VALUE) {
      setIsOpen(false);
      setShowAddModal(true);
      return;
    }

    setIsOpen(false);

    if (newValue === value) return;

    if (onUpdate) {
      onUpdate(rowId, columnId, newValue);
    }

    if (onSave) {
      setIsSaving(true);
      onSave(rowId, columnId, value, newValue)
        .then(() => setIsSaving(false))
        .catch(error => {
          setIsSaving(false);
          console.error('Background save failed:', error);
        });
    }
  }, [rowId, columnId, value, onUpdate, onSave]);

  const handleClick = useCallback(() => {
    if (hasEditPermission && !isDragging) {
      setIsOpen(!isOpen);
    }
  }, [hasEditPermission, isDragging, isOpen]);

  const handleDragStart = useCallback((e) => {
    if (onDragFillStart) {
      onDragFillStart(e, rowIndex, columnId, value || '');
    }
  }, [onDragFillStart, rowIndex, columnId, value]);

  const handleLocationAdded = useCallback((newLocation) => {
    if (onLocationAdded) {
      onLocationAdded(newLocation);
    }
    if (onUpdate) {
      onUpdate(rowId, columnId, newLocation.location_name);
    }
    if (onSave) {
      setIsSaving(true);
      onSave(rowId, columnId, value, newLocation.location_name)
        .then(() => setIsSaving(false))
        .catch(error => {
          setIsSaving(false);
          console.error('Background save failed:', error);
        });
    }
  }, [onLocationAdded, onUpdate, onSave, rowId, columnId, value]);

  if (!hasEditPermission) {
    return (
      <div style={{ fontSize: '12px', textAlign: 'center' }}>
        {value || ''}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        ref={triggerRef}
        onClick={handleClick}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '2px 4px',
          cursor: 'pointer',
          fontSize: '12px',
          color: value ? '#212529' : '#999',
          outline: isRetrying ? '2px solid orange' : 'none',
          pointerEvents: isDragging ? 'none' : 'auto',
          userSelect: 'none',
        }}
        title={isRetrying ? 'Retrying save...' : value || 'Select shop'}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'center' }}>
          {value || '-- Select --'}
        </span>
        <span style={{ fontSize: '9px', color: '#999', marginLeft: '2px' }}>▼</span>
      </div>

      {isOpen && createPortal(
        <>
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
          />
          {(() => {
            const pos = getPosition(triggerRef.current);
            return (
              <div
                style={{
                  position: 'fixed',
                  ...(pos.openUpward
                    ? { bottom: `${window.innerHeight - pos.top}px` }
                    : { top: `${pos.top}px` }),
                  ...(pos.right !== undefined
                    ? { right: `${pos.right}px` }
                    : { left: `${pos.left}px` }),
                  transform: pos.transform,
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  zIndex: 9999,
                  minWidth: '140px',
                  maxHeight: `${Math.min(250, pos.maxHeight)}px`,
                  overflowY: 'auto'
                }}
              >
                <div
                  onClick={() => handleSelect('')}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    backgroundColor: !value ? '#e7f3ff' : 'white',
                    borderBottom: '1px solid #f0f0f0',
                    color: '#999'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = !value ? '#e7f3ff' : 'white'}
                >
                  -- Select --
                </div>
                {locations.map(loc => (
                  <div
                    key={loc.id}
                    onClick={() => handleSelect(loc.location_name)}
                    style={{
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      backgroundColor: value === loc.location_name ? '#e7f3ff' : 'white',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#333'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === loc.location_name ? '#e7f3ff' : 'white'}
                  >
                    {loc.location_name}
                  </div>
                ))}
                <div
                  onClick={() => handleSelect(ADD_NEW_VALUE)}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    color: '#3b82f6',
                    fontStyle: 'italic',
                    borderTop: '1px solid #dee2e6'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  + Add New Location...
                </div>
              </div>
            );
          })()}
        </>,
        document.body
      )}

      {(isSaving || isRetrying) && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            right: '20px',
            transform: 'translateY(-50%)',
            width: '10px',
            height: '10px',
            border: `2px solid ${isRetrying ? '#ffe4c4' : '#f3f3f3'}`,
            borderTop: `2px solid ${isRetrying ? '#ff8c00' : '#007bff'}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
          title={isRetrying ? 'Retrying save...' : 'Saving...'}
        />
      )}
      
      {showDragHandle && hasEditPermission && onDragFillStart && (
        <div
          onMouseDown={handleDragStart}
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '7px',
            height: '7px',
            backgroundColor: '#007bff',
            cursor: 'crosshair',
            borderRadius: '1px',
            zIndex: 10,
            boxShadow: '0 0 1px rgba(0,0,0,0.3)'
          }}
          title="Drag to fill cells"
        />
      )}
      
      <AddFabricationLocationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onLocationAdded={handleLocationAdded}
        existingLocations={locations}
      />
    </div>
  );
});

FabricationShopCell.displayName = 'FabricationShopCell';

export default FabricationShopCell;
